import { ReactNode, useEffect, useState } from 'react'

import { useTheme } from '@emotion/react'
import { useTranslation } from 'react-i18next'

import { DeviceMobileIcon } from '@arcade/web/icons/react'
import { ArcadeButton, Flex, spacing, Text, Toast } from '@cash-design-system/react'
import { TriggerCampaignRequestId } from '@cash-web/protos/squareup/cashfrontend/app.pb'
import { useMutationTriggerCampaign } from '@cash-web/shared-data-access-api/react-query'
import { useExperiment } from '@cash-web/shared-data-access-experiments'
import { useLogCashWebCdfEvent } from '@cash-web/shared-feature-analytics'
import { getEnvironmentAtRunTime } from '@cash-web/shared-util-environment'
import { WebInteractClick } from '@squareup/customer-data-events-web'

export interface RedirectToMobileProps {
  deepLinkPath?: `/${string}`
  clientRouteUrl?: string
  isLoadingQR?: boolean
  children?: ReactNode
  bodyText?: string
}
// TODO - Move useQRImage to a shared library
const useQRImage = ({ path }: { path?: `/${string}` }) => {
  const theme = useTheme()

  const [loadedSrc, setLoadedSrc] = useState<string | null>(null)

  useEffect(() => {
    if (!path) return

    const src = new URL(
      '/qr' + path,
      getEnvironmentAtRunTime() === 'production' ? 'https://cash.app' : 'https://cashstaging.app'
    )

    src.searchParams.set('fg', theme.arcadeColors.semantic.textStandard)
    src.searchParams.set('bg', '#00000000' /* transparent */)
    src.searchParams.set('format', 'svg')
    src.searchParams.set('logoColor', theme.arcadeColors.semantic.iconBrand)

    const img = new Image()
    img.src = src.href
    img.onload = () => setLoadedSrc(src.href)
    img.onerror = () => setLoadedSrc(src.href)
  }, [path, theme])

  if (loadedSrc) {
    return { src: loadedSrc, isLoading: false } as const
  }

  return { src: null, isLoading: true } as const
}

export const RedirectToMobile = ({
  deepLinkPath,
  clientRouteUrl,
  isLoadingQR = false,
  children,
  bodyText,
}: RedirectToMobileProps) => {
  const { t } = useTranslation()
  const {
    mutate: triggerCampaign,
    isLoading,
    isSuccess,
  } = useMutationTriggerCampaign({
    mutationOptions: {
      onSuccess: () => {
        setShowSuccessToast(true)
      },
      onError: () => {
        setShowErrorToast(true)
      },
    },
  })
  const logCdfEvent = useLogCashWebCdfEvent()
  const [showSuccessToast, setShowSuccessToast] = useState(false)
  const [showErrorToast, setShowErrorToast] = useState(false)

  const { src, isLoading: isImageLoading } = useQRImage({ path: deepLinkPath || '/download' })

  const isPushNotificationEnabled =
    useExperiment('cash-web-continue-in-mobile-push-notification')?.variant_value === 'treatment'

  const headerText = t('View in the app', { context: 'redirect to mobile header text' })
  const defaultBodyText = t('Open Cash App on your phone to view more details.', {
    context: 'redirect to mobile body text',
  })
  const displayBodyText = bodyText || defaultBodyText
  const successToastMessage = t('Notification sent. Check your phone to continue.', {
    context: 'success toast when push notification is sent',
  })
  const errorToastMessage = t('Something went wrong. Please try again.', {
    context: 'error toast when push notification fails',
  })

  const handleSendPushNotification = () => {
    logCdfEvent(
      WebInteractClick({
        componentName: 'RedirectToMobile',
        componentType: 'Send To Phone',
      })
    )

    triggerCampaign({
      id: TriggerCampaignRequestId.WEB_UNSUPPORTED_CONTINUE_IN_MOBILE,
      client_route_url: clientRouteUrl,
    })
  }

  if (isImageLoading) {
    return null
  }
  return (
    <Flex flexDirection="column" gap={spacing.defaultContent}>
      {children}
      <Flex flexDirection="column" gap={spacing.s}>
        <DeviceMobileIcon size={32} />
        <Flex flexDirection="column" gap={spacing.s}>
          <Text typography="pageTitle" as="h2" color="textStandard">
            {headerText}
          </Text>
          <Text typography="body" color="textSubtle">
            {displayBodyText}
          </Text>
        </Flex>
      </Flex>
      {!isImageLoading && (
        <Flex justifyContent="center">
          <img
            src={src}
            alt={t('QR code to view flow in Cash App', { context: 'QR code alt text' })}
            width={200}
            height={200}
          />
        </Flex>
      )}
      {clientRouteUrl && isPushNotificationEnabled && (
        <ArcadeButton size="cta" prominence="standard" onClick={handleSendPushNotification} isLoading={isLoading}>
          {isSuccess
            ? t('Send again', { context: 'redirect to mobile button to resend push notification' })
            : t('Send to Cash App', { context: 'redirect to mobile button to send push notification' })}
        </ArcadeButton>
      )}
      {showSuccessToast && (
        <Toast
          headline={successToastMessage}
          dismissTimeout={3000}
          data-testid="redirect-to-mobile-success-toast"
          onClose={() => setShowSuccessToast(false)}
        />
      )}
      {showErrorToast && (
        <Toast
          headline={errorToastMessage}
          dismissTimeout={3000}
          data-testid="redirect-to-mobile-error-toast"
          onClose={() => setShowErrorToast(false)}
        />
      )}
    </Flex>
  )
}
